import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  ViewChild,
  forwardRef,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { CommonModule } from "@angular/common";
import { NgIconsModule } from "@ng-icons/core";
import { isNgIconName } from "../utils/icon";

/**
 * EVA Input - Composant 100% custom
 *
 * Input entièrement personnalisé utilisant les tokens EVA (pas de PrimeNG).
 * Supporte icônes, clearable, tailles et états.
 *
 * @example
 * // Input simple
 * <eva-input label="Nom" [(value)]="name" required></eva-input>
 *
 * // Input avec icône et helper
 * <eva-input label="Email" type="email" icon="pi pi-envelope" helper="Adresse professionnelle"></eva-input>
 *
 * // Input clearable avec erreur
 * <eva-input label="Recherche" [clearable]="true" error="Champ invalide"></eva-input>
 */
@Component({
  selector: "eva-input",
  standalone: true,
  imports: [CommonModule, NgIconsModule],
  templateUrl: "./eva-input.component.html",
  styleUrls: ["./eva-input.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EvaInputComponent),
      multi: true,
    },
  ],
})
export class EvaInputComponent implements ControlValueAccessor, AfterViewInit {
  // ==========================================================================
  // Native input Inputs
  // ==========================================================================

  /** Type de l'input */
  @Input() type: "text" | "email" | "password" | "tel" | "url" | "date" | "time" = "text";

  /** Pas d'incrémentation pour `type="time"` (ex: "900" = pas de 15 min). */
  @Input() step: string | number | null = null;

  /** Placeholder */
  @Input() placeholder: string = "";

  /** Désactive l'input */
  @Input() disabled: boolean = false;

  /** Input en lecture seule */
  @Input() readonly: boolean = false;

  /** Longueur maximale */
  @Input() maxlength: number | null = null;

  /** Autocomplete */
  @Input() autocomplete: string = "off";

  // ==========================================================================
  // EVA-specific Inputs
  // ==========================================================================

  /**
   * Label affiché au-dessus de l'input.
   * Ne pas y mettre d'astérisque : `required` l'ajoute déjà (sinon rendu « Nom ** »).
   */
  @Input() label: string = "";

  /** Texte d'aide affiché sous l'input (masqué si error est présent) */
  @Input() helper: string = "";

  /** Message d'erreur (affiché en rouge, remplace helper) */
  @Input() error: string = "";

  /** Ajoute un * au label (le label lui-même ne doit donc pas en contenir) */
  @Input() required: boolean = false;

  /** Taille de l'input */
  @Input() size: "small" | "medium" | "large" | "inline" = "medium";

  /** Layout du host : block pour les formulaires, inline pour les toolbars/filtres */
  @Input() layout: "block" | "inline" = "block";

  /**
   * Largeur fluide du host. À désactiver pour laisser le host prendre sa largeur naturelle.
   *
   * N'a d'effet qu'en `layout="inline"` : les règles de largeur sont toutes portées par
   * `:host(.eva-input-host--inline)`. En `layout="block"` (défaut) le host est en
   * `display: block` et `[fluid]="false"` est un no-op — poser la largeur côté feature.
   */
  @Input() fluid: boolean = true;

  /** Variante visuelle : rectangular (standard) ou rounded (pill) */
  @Input() variant: "rounded" | "rectangular" = "rectangular";

  /** Icône à gauche (PrimeNG: "pi pi-search" ou HeroIcon: "heroMagnifyingGlass") */
  @Input() icon: string = "";

  /** Icône à droite (PrimeNG: "pi pi-calendar" ou HeroIcon: "heroCalendar") */
  @Input() iconRight: string = "";

  /** Affiche un bouton clear quand l'input a une valeur */
  @Input() clearable: boolean = false;

  /** Affiche le bouton verrou/déverrouillage à gauche du champ */
  @Input() lockable: boolean = false;

  /** État verrouillé du champ (bloque la modification de la valeur) */
  @Input() locked: boolean = false;

  /** Active le raccourci clavier Ctrl/Cmd + Shift + L pour verrouiller/déverrouiller */
  @Input() lockShortcutEnabled: boolean = true;

  /** ID unique pour l'input (auto-généré si non fourni) */
  @Input() inputId: string = `eva-input-${Math.random().toString(36).substr(2, 9)}`;

  /** Focus automatique au chargement du composant */
  @Input() autofocus: boolean = false;

  // ==========================================================================
  // ViewChild
  // ==========================================================================

  @ViewChild("inputElement", { static: false }) inputElement?: ElementRef<HTMLInputElement>;

  // ==========================================================================
  // Outputs
  // ==========================================================================

  /** Émis quand la valeur change — pour le two-way binding [(value)]. Voir `eva-number-input` pour les nombres. */
  @Output() valueChange = new EventEmitter<string>();

  /** Émis quand l'état verrouillé change — pour le two-way binding [(locked)] */
  @Output() lockedChange = new EventEmitter<boolean>();

  /** Émis quand l'input reçoit le focus */
  @Output() onFocus = new EventEmitter<FocusEvent>();

  /** Émis quand l'input perd le focus */
  @Output() onBlur = new EventEmitter<FocusEvent>();

  // ==========================================================================
  // Internal state
  // ==========================================================================

  _value: string = "";
  isFocused: boolean = false;
  showPassword: boolean = false;

  @HostBinding("class.eva-input-host--inline")
  get isInlineLayout(): boolean {
    return this.layout === "inline";
  }

  @HostBinding("class.eva-input-host--fluid")
  get isFluid(): boolean {
    return this.fluid;
  }

  // ==========================================================================
  // Two-way binding [(value)]
  // ==========================================================================

  @Input()
  get value(): string {
    return this._value;
  }
  set value(val: string | number | null) {
    const incoming = this.coerceToString(val);
    if (incoming !== this._value) {
      this._value = incoming;
      this.onChange(this._value);
      this.valueChange.emit(this._value);
    }
  }

  // ==========================================================================
  // ControlValueAccessor
  // ==========================================================================

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: string | number | null): void {
    this._value = this.coerceToString(value);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // ==========================================================================
  // Computed
  // ==========================================================================

  /**
   * Détermine si l'input a une icône à gauche
   */
  get hasLeftIcon(): boolean {
    return !!this.icon;
  }

  /**
   * Détermine si le bouton lock/unlock est affiché
   */
  get hasLockToggle(): boolean {
    return this.lockable;
  }

  /**
   * Détermine si l'input a un élément à gauche (lock ou icône)
   */
  get hasLeftElement(): boolean {
    return this.hasLockToggle || this.hasLeftIcon;
  }

  /**
   * Détermine si deux éléments sont présents à gauche (lock + icône)
   */
  get hasDualLeftElement(): boolean {
    return this.hasLockToggle && this.hasLeftIcon;
  }

  /**
   * L'icône gauche est une HeroIcon (commence par "hero")
   */
  get isHeroIcon(): boolean {
    return isNgIconName(this.icon);
  }

  /**
   * L'icône droite est une HeroIcon (commence par "hero")
   */
  get isHeroIconRight(): boolean {
    return isNgIconName(this.iconRight);
  }

  /**
   * Détermine si l'input a un élément à droite (icône, clear button ou password toggle)
   */
  get hasRightElement(): boolean {
    return !!this.iconRight || (this.clearable && !!this._value) || this.isPasswordType;
  }

  /**
   * Affiche le bouton clear ?
   */
  get showClear(): boolean {
    return this.clearable && !!this._value && !this.disabled && !this.readonly && !this.locked;
  }

  /**
   * L'input est de type password ?
   */
  get isPasswordType(): boolean {
    return this.type === "password";
  }

  /** Type effectif de l'input (text quand le password est visible, sinon le type original). */
  get effectiveType(): string {
    if (this.isPasswordType && this.showPassword) {
      return "text";
    }
    return this.type;
  }

  /** Convertit une valeur entrante (string | number | null | undefined) vers la string visible. */
  private coerceToString(val: string | number | null | undefined): string {
    if (val == null) return "";
    if (typeof val === "number") return Number.isFinite(val) ? String(val) : "";
    return val;
  }

  // ==========================================================================
  // Lifecycle
  // ==========================================================================

  ngAfterViewInit(): void {
    if (this.autofocus && this.inputElement) {
      // setTimeout pour éviter ExpressionChangedAfterItHasBeenCheckedError
      setTimeout(() => {
        this.inputElement?.nativeElement.focus();
      }, 0);
    }
  }

  focus(): void {
    setTimeout(() => {
      this.inputElement?.nativeElement?.focus();
    }, 0);
  }

  // ==========================================================================
  // Methods
  // ==========================================================================

  onInput(event: Event): void {
    if (this.locked) {
      return;
    }

    const input = event.target as HTMLInputElement;

    this._value = input.value;
    this.onChange(this._value);
    this.valueChange.emit(this._value);
  }

  /** Listener (change) — utilisé pour les types qui ne déclenchent pas (input) fiablement
   * (ex: type="time" au blur dans certains navigateurs). Évite la double émission sur
   * les autres types qui déclenchent déjà (input). */
  onChangeEvent(event: Event): void {
    if (this.type === "time") {
      this.onInput(event);
    }
  }

  handleFocus(event: FocusEvent): void {
    this.isFocused = true;
    this.onFocus.emit(event);
  }

  handleBlur(event: FocusEvent): void {
    this.isFocused = false;
    this.onTouched();
    this.onBlur.emit(event);
  }

  clear(): void {
    if (this.locked) {
      return;
    }

    this._value = "";
    this.onChange(this._value);
    this.valueChange.emit(this._value);
  }

  toggleLock(): void {
    if (!this.lockable || this.disabled || this.readonly) {
      return;
    }

    this.locked = !this.locked;
    this.lockedChange.emit(this.locked);
    if (this.locked) {
      this.onTouched();
    }
  }

  handleKeydown(event: KeyboardEvent): void {
    this.handleLockShortcut(event);
  }

  private handleLockShortcut(event: KeyboardEvent): boolean {
    if (!this.lockable || !this.lockShortcutEnabled || this.disabled || this.readonly) {
      return false;
    }

    const isLockShortcut = (event.ctrlKey || event.metaKey) && event.shiftKey && event.key.toLowerCase() === "l";
    if (!isLockShortcut) {
      return false;
    }

    event.preventDefault();
    this.toggleLock();
    return true;
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }
}
